import React, { useRef, useState } from 'react';
import { ArrowRight, Mic, MessageSquare, Layers, BarChart2 } from 'lucide-react';
import { InteractionMode } from '../../types';
import { Toast } from 'primereact/toast';
import CryptoJS from 'crypto-js';
import { login } from '../services/authService';
import { useNavigate } from 'react-router-dom';
import { handleAuthDetails, handleInteractionMode } from '../redux/persistentSlice';
import { AppDispatch } from '../redux/store';
import { useDispatch } from 'react-redux';

const LoginScreen = () => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [selectedMode, setSelectedMode] = useState<InteractionMode>('hybrid');
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const secretKey = process.env.REACT_APP_ENCRYPTION;
  const iv = 'BBBBBBBBBBBBBBBB';

  const showError = (message = 'Invalid username or password') => {
    toast.current?.show({
      severity: 'error',
      summary: 'Login Failed',
      detail: message,
      life: 3000,
    });
  };

  const encryptPassword = (password: string) => {
    const encrypted = CryptoJS.AES.encrypt(
      password,
      CryptoJS.enc.Utf8.parse(secretKey ?? ""),
      {
        iv: CryptoJS.enc.Utf8.parse(iv),
        mode: CryptoJS.mode.ECB,
      }
    );
    return encrypted.toString();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) {
      showError('Username and password are required');
      return;
    }

    setIsLoading(true);
    try {
      const encryptedPassword = encryptPassword(password.trim());

      const { result, error } = await login(username, encryptedPassword);
      dispatch(handleAuthDetails(result))
      dispatch(handleInteractionMode(selectedMode))

      if (error || result?.error) {
        showError();
        throw error || result.error;
      }
      else {
        // Store authentication tokens from login response
        // The result is already response.data from the service
        if (result?.access_token) {
          localStorage.setItem("access_token", result.access_token);
        }
        if (result?.refresh_token) {
          localStorage.setItem("refresh_token", result.refresh_token);
        }
        if (result?.user_id) {
          localStorage.setItem("user_id", result.user_id);
        }
        // Store login status
        localStorage.setItem("isLogged", "true");
        localStorage.setItem('loginTime', new Date() as any);
        localStorage.setItem('session_id', result.session_id)
        toast.current?.show({
          severity: 'success',
          summary: 'Login Successful',
          detail: 'Redirecting...',
          life: 1000,
        });

        setTimeout(() => {
          setIsLoading(false);
          navigate('/chat');
          // window.location.href = '/chat';
        }, 800);
        return;
      }
    } catch (err) {
      console.error('Login failed:', err);
      showError();
    }
    setIsLoading(false);
  };

  const modeOptions: { id: InteractionMode; label: string; icon: React.ElementType; desc: string }[] = [
    { id: 'text', label: 'Text Based', icon: MessageSquare, desc: 'Silent, read-only interface.' },
    { id: 'voice', label: 'Voice Based', icon: Mic, desc: 'Hands-free audio experience.' },
    { id: 'hybrid', label: 'Hybrid', icon: Layers, desc: 'Fluid voice and text control.' },
  ];

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-slate-50 relative overflow-hidden font-sans">
      <Toast ref={toast} position="bottom-right" />

      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-indigo-100 rounded-full blur-[80px] opacity-60" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-blue-100 rounded-full blur-[100px] opacity-60" />
      </div>

      <div className="w-full max-w-5xl h-full md:h-auto z-10 grid grid-cols-1 md:grid-cols-2 gap-0 overflow-hidden rounded-none md:rounded-2xl shadow-2xl bg-white">
        {/* Left Side */}
        <div className="relative bg-indigo-600 p-12 flex flex-col justify-between overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 to-blue-700" />
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" />

          <div className="relative z-10 pt-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/20">
                <BarChart2 className="text-white w-6 h-6" />
              </div>
              <span className="text-indigo-100 font-medium tracking-wide text-sm uppercase">Analytics Portal</span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-white leading-tight mb-6">
              Visual Comfort <br />
              <span className="italic font-normal text-indigo-200">&amp; Co.</span>
            </h1>
            <p className="text-indigo-100 text-lg leading-relaxed max-w-sm">
              Access real-time business intelligence, generated instantly through natural language conversation.
            </p>
          </div>

          <div className="relative z-10 mt-12">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-bold text-indigo-300 uppercase tracking-wider">Powered by Rysun</span>
            </div>
          </div>
        </div>

        {/* Right Side */}
        <div className="p-12 md:p-14 flex flex-col justify-center bg-white">
          <div className="w-full">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Welcome to Conversational BI</h2>
              <p className="text-slate-500">Please sign in to continue.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">Username</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-medium"
                    placeholder="Enter your ID"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-medium"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Interaction Mode</label>
                <div className="grid grid-cols-3 gap-3">
                  {modeOptions.map((mode) => (
                    <button
                      key={mode.id}
                      type="button"
                      onClick={() => setSelectedMode(mode.id)}
                      className={`relative flex items-center justify-center p-3 rounded-xl transition-all text-left border ${selectedMode === mode.id
                          ? 'border-indigo-600 bg-indigo-50 shadow-sm'
                          : 'border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300'
                        }`}
                      title={mode.label}
                    >
                      <div
                        className={`p-2 rounded-lg transition-colors ${selectedMode === mode.id ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500'
                          }`}
                      >
                        <mode.icon size={18} strokeWidth={2} />
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm tracking-wide uppercase py-4 rounded-xl shadow-lg shadow-indigo-600/20 hover:shadow-indigo-600/30 transition-all flex items-center justify-center gap-2 group mt-6 disabled:opacity-70 disabled:cursor-not-allowed transform active:scale-[0.98]"
              >
                {isLoading ? (
                  <span>Signing In...</span>
                ) : (
                  <>
                    <span>Let's start</span>
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
